import { useReducedMotion } from 'framer-motion';
import { useEffect, useState } from 'react';

import { useReduceBizyMotionPreference } from './bizy-motion-preferences';
import { BizyState } from './types';

/**
 * Pose loop Bizy runs in the nav when she has something waiting: she waves,
 * glances toward the label, points at it, then glances back before repeating.
 */
const ATTENTION_POSE_LOOP = [
	{ state: 'waving', holdMs: 2200 },
	{ state: 'lookingRight', holdMs: 900 },
	{ state: 'pointingRight', holdMs: 1600 },
	{ state: 'lookingRight', holdMs: 900 },
] as const satisfies readonly { state: BizyState; holdMs: number }[];

/**
 * Cycles the attention pose loop while `enabled`. Holds the opening wave when
 * motion is reduced, so the pose still reads as "Bizy wants you" without the
 * animation.
 */
export const useBizyAttentionPose = (enabled: boolean): BizyState => {
	const prefersReducedMotion = useReducedMotion();
	const [reduceBizyMotion] = useReduceBizyMotionPreference();
	const shouldHold = Boolean(prefersReducedMotion) || reduceBizyMotion;
	const [step, setStep] = useState(0);

	useEffect(() => {
		if (!enabled || shouldHold) {
			setStep(0);
			return;
		}

		const timer = setTimeout(() => setStep((current) => (current + 1) % ATTENTION_POSE_LOOP.length), ATTENTION_POSE_LOOP[step].holdMs);
		return () => clearTimeout(timer);
	}, [enabled, shouldHold, step]);

	return ATTENTION_POSE_LOOP[step].state;
};
